"use client";
import { lazy, Suspense, useEffect, useState } from "react";
import type { SizeMappingEntry } from "./AdUnit";

const AdUnit = lazy(() => import("./AdUnit"));
const AdSenseUnit = lazy(() => import("./AdSenseUnit"));

export interface AdManagerProps {
  componentIndex: number;
  componentAds: any;
  position: "top" | "bottom";
  adSenseEnabled: boolean;
  adSenseId: string;
  gamIsEnabled: boolean;
  gamId: string;
  secondaryArgs: any;
  sharedAdDetails?: any;
  onFetchAdDetails?: (componentIndex: number, adDetails: any) => void;
  /** Override the default GAM slot sizes ([728, 90]). */
  sizes?: number[][];
  /** GPT size mapping breakpoints forwarded to AdUnit. */
  adSizeMapping?: SizeMappingEntry[];
}

// Global cache to prevent duplicate API calls
const adDetailsCache: Record<string, any> = {};
const fetchingPromises: Record<string, Promise<any>> = {};

const AdManager = ({
  componentIndex,
  componentAds,
  position,
  adSenseEnabled,
  adSenseId,
  gamIsEnabled,
  gamId,
  secondaryArgs,
  sharedAdDetails,
  onFetchAdDetails,
  sizes = [[728, 90]],
  adSizeMapping,
}: AdManagerProps) => {
  const [adDetails, setAdDetails] = useState<any>({});

  const componentAdSenseAds =
    componentAds?.selected_ads?.filter((ad: any) => ad.content_type === "adsense") || [];
  const hasComponentAds = componentAdSenseAds.length > 0;
  const componentAdPosition = componentAds?.ad_position || "top";

  // Fetch ads data for this specific component (only once per component)
  useEffect(() => {
    if (!hasComponentAds) return;

    const cacheKey = `component_${componentIndex}`;

    // Check if we already have data in cache or shared props
    if (adDetailsCache[cacheKey]) {
      setAdDetails({ [cacheKey]: adDetailsCache[cacheKey] });
      return;
    }

    if (sharedAdDetails?.[cacheKey]) {
      setAdDetails({ [cacheKey]: sharedAdDetails[cacheKey] });
      adDetailsCache[cacheKey] = sharedAdDetails[cacheKey];
      return;
    }

    // Check if already fetching
    if (cacheKey in fetchingPromises) {
      fetchingPromises[cacheKey].then((data) => {
        if (data) {
          setAdDetails({ [cacheKey]: data });
        }
      });
      return;
    }

    // Start fetching - make individual calls for each ad since they have different content paths
    const fetchAdsData = async () => {
      try {
        // Make individual API calls for each ad
        const adPromises = componentAdSenseAds.map(async (_ad: any) => {
          return await null; // Replace with actual API call
        });

        const adResults = await Promise.all(adPromises);

        // Combine all ad results
        const combinedData = adResults.filter((result) => result).flat();

        if (combinedData.length > 0) {
          adDetailsCache[cacheKey] = combinedData;
          setAdDetails({ [cacheKey]: combinedData });
          onFetchAdDetails?.(componentIndex, combinedData);
        }
        return combinedData;
      } catch (error) {
        console.error("Error fetching ads data:", error);
        return null;
      }
    };

    fetchingPromises[cacheKey] = fetchAdsData();
    fetchingPromises[cacheKey].finally(() => {
      delete fetchingPromises[cacheKey];
    });
  }, [
    componentAdSenseAds,
    hasComponentAds,
    componentIndex,
    secondaryArgs,
    sharedAdDetails,
    onFetchAdDetails,
  ]);

  // Don't render if no ads or wrong position
  if (!hasComponentAds || componentAdPosition !== position) {
    return null;
  }

  // Get ads details from state
  const ads_details = adDetails[`component_${componentIndex}`]?.[0] || null;
  const { ad_slot } = ads_details || {};

  return (
    <div data-testid={`ads-${position}`}>
      <Suspense fallback={null}>
        {componentAdSenseAds.map((_ad: any, adIndex: number) =>
          adSenseEnabled ? (
            <AdSenseUnit
              // eslint-disable-next-line react/no-array-index-key
              key={`adsense-comp-${componentIndex}-${position}-${adIndex}`}
              adClient={adSenseId}
              adSlot={ad_slot?.[0] || ""}
            />
          ) : gamIsEnabled ? (
            <AdUnit
              // eslint-disable-next-line react/no-array-index-key
              key={`gam-comp-${componentIndex}-${position}-${adIndex}`}
              adUnitPath={`/${gamId}`}
              // sizes={[[728, 90]]}
              elementId={`gam-ad-comp-${componentIndex}-${position}-${adIndex}`}
              sizes={sizes}
              sizeMapping={adSizeMapping}
            />
          ) : null,
        )}
      </Suspense>
    </div>
  );
};

export default AdManager;
